import { useState, useEffect } from 'react';
import { Contract, Person, LegalEntity } from '../types/Contract';

const API_BASE = '/api/contracts';

export const useContracts = () => {
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Загрузка договоров с сервера
  const loadContracts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(API_BASE);
      if (!response.ok) {
        throw new Error('Ошибка загрузки договоров');
      }
      const data = await response.json();
      setContracts(data);
    } catch (error) {
      console.error('Ошибка загрузки договоров:', error);
      setError('Не удалось загрузить договоры');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContracts();
  }, []);

  const saveContract = async (contract: Contract) => {
    try {
      setError(null);
      const response = await fetch(`${API_BASE}/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contract),
      });

      if (!response.ok) {
        throw new Error('Ошибка сохранения договора');
      }

      const result = await response.json();
      setContracts(prev => [...prev, result.contract]);
      console.log('✅ Договор сохранен в файл:', contract.id);
    } catch (error) {
      console.error('Ошибка сохранения договора:', error);
      setError('Не удалось сохранить договор');
      throw error;
    }
  };

  const updateContract = async (id: string, updatedContract: Contract) => {
    try {
      setError(null);
      const response = await fetch(`${API_BASE}/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedContract),
      });

      if (!response.ok) {
        throw new Error('Ошибка обновления договора');
      }

      const result = await response.json();
      setContracts(prev => prev.map(contract =>
        contract.id === id ? result.contract : contract
      ));
      console.log('✅ Договор обновлен в файле:', id);
    } catch (error) {
      console.error('Ошибка обновления договора:', error);
      setError('Не удалось обновить договор');
      throw error;
    }
  };

  const deleteContract = async (id: string) => {
    try {
      setError(null);
      const response = await fetch(`${API_BASE}/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Ошибка удаления договора');
      }

      setContracts(prev => prev.filter(contract => contract.id !== id));
      console.log('✅ Договор удален из файла:', id);
    } catch (error) {
      console.error('Ошибка удаления договора:', error);
      setError('Не удалось удалить договор');
      throw error;
    }
  };

  const getContract = (id: string) => {
    return contracts.find(contract => contract.id === id);
  };

  // Получение уникальных продавцов из всех договоров
  const getUniqueSellers = (): (Person | LegalEntity)[] => {
    const sellersMap = new Map<string, Person | LegalEntity>();

    contracts.forEach(contract => {
      const seller = contract.seller;
      let key = '';

      if ((seller as any).type === 'legal_entity') {
        const legalSeller = seller as LegalEntity;
        key = `legal_${legalSeller.inn}_${legalSeller.kpp || ''}`;
      } else {
        const personSeller = seller as Person;
        key = `person_${personSeller.passportSeries}_${personSeller.passportNumber}`;
      }

      if (!sellersMap.has(key)) {
        sellersMap.set(key, seller);
      }
    });

    return Array.from(sellersMap.values());
  };

  // Получение уникальных покупателей из всех договоров
  const getUniqueBuyers = (): (Person | LegalEntity)[] => {
    const buyersMap = new Map<string, Person | LegalEntity>();

    contracts.forEach(contract => {
      const buyer = contract.buyer;
      let key = '';

      if ((buyer as any).type === 'legal_entity') {
        const legalBuyer = buyer as LegalEntity;
        key = `legal_${legalBuyer.inn}_${legalBuyer.kpp || ''}`;
      } else {
        const personBuyer = buyer as Person;
        key = `person_${personBuyer.passportSeries}_${personBuyer.passportNumber}`;
      }

      if (!buyersMap.has(key)) {
        buyersMap.set(key, buyer);
      }
    });

    return Array.from(buyersMap.values());
  };

  // Изменение статуса договора
  const updateContractStatus = async (id: string, newStatus: Contract['status']) => {
    try {
      setError(null);
      const contract = contracts.find(c => c.id === id);
      if (!contract) {
        throw new Error('Договор не найден');
      }

      const updatedContract = { ...contract, status: newStatus };

      const response = await fetch(`${API_BASE}/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedContract),
      });

      if (!response.ok) {
        throw new Error('Ошибка изменения статуса');
      }

      const result = await response.json();
      setContracts(prev => prev.map(contract =>
        contract.id === id ? result.contract : contract
      ));
      console.log('✅ Статус договора изменен:', id, '→', newStatus);
    } catch (error) {
      console.error('Ошибка изменения статуса:', error);
      setError('Не удалось изменить статус договора');
      throw error;
    }
  };

  return {
    contracts,
    loading,
    error,
    saveContract,
    updateContract,
    deleteContract,
    getContract,
    reloadContracts: loadContracts,
    getUniqueSellers,
    getUniqueBuyers,
    updateContractStatus
  };
}; 